import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowDown, X, MessageSquare, Paperclip, Search, Mic, Square, Send, Phone, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { listMessages as bridgeListMessages, listMessagesBefore as bridgeListMessagesBefore, markRead as bridgeMarkRead, reactToMessage as bridgeReact, forwardMessage as bridgeForward, clearConversation as bridgeClear } from '@/lib/chatBridge';
import { AppEmbedPreview } from '@/components/embed/AppEmbedPreview';
import { createLogger } from '@/lib/logger';
import { friendlyError } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { confirmDialog } from '@/lib/dialogs';
import { EmojiPicker } from '@/components/ui/EmojiPicker';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useIsDarkTheme } from '@/hooks/useIsDarkTheme';
import { useConnectionStore } from '@/lib/connection';
import { sendChatMessage, useChatQueueStore } from '@/lib/chatQueue';
import { uploadFile } from '@/lib/chunkedUpload';
import { useToastStore } from '@/store/toastStore';
import { fileBytes } from '@/lib/fileBytes';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { Virtuoso, type VirtuosoHandle, type ListRange } from 'react-virtuoso';
import { MessageBubble, type Msg as BubbleMsg } from '@/components/chat/MessageBubble';
import { VideoCallPanel } from '@/components/chat/VideoCallPanel';
import { RemotePanel } from '@/components/remote/RemotePanel';
import { publishRemoteSignal, remoteTopic } from '@/lib/remote/remoteSession';

const log = createLogger('ChatThread');


export function lastSeenLabel(lastSeen: string): string {
  if (!lastSeen) return '';
  const t = new Date(lastSeen).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 2 * 60 * 1000) return 'онлайн';
  const d = new Date(t);
  const now = new Date();
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return `был(а) сегодня в ${time}`;
  const date = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  return `был(а) ${date} в ${time}`;
}

// Индикатор офлайн-очереди: «⏳ N в очереди» над полем ввода (SSoT — chatQueue)
export function QueuedIndicator({ conversationId }: { conversationId: string }) {
  const pending = useChatQueueStore((s) => s.pendingByConv[conversationId] || 0);
  if (pending === 0) return null;
  return (
    <div className="px-3 pt-1 text-[10px] text-amber-600 flex items-center gap-1">
      <span>⏳</span>
      <span>{pending === 1 ? '1 сообщение в очереди — будет отправлено при восстановлении связи' : `${pending} сообщений в очереди — будут отправлены при восстановлении связи`}</span>
    </div>
  );
}
